import React from 'react'
import PropTypes from 'prop-types'

import { Map } from 'immutable'
import { Transition } from 'react-transition-group'

import ContextSubscriber from '@aytm/lib/common/components/decorators/ContextSubscriber'
import ManagedRenderComponent from '@aytm/lib/common/components/ManagedRenderComponent'
import copyToClipboard from '@aytm/lib/common/copy-to-clipboard'

import {
  reportStatePath,
  updateListState,
  deleteListItem,
  updateItemModel,
  updateItemShareLink
} from '@aytm/app/stats/page/sidebar/reports/actions'

import { encodeText, decodeText } from '@aytm/app/stats/lib/helpers'

import {
  isSkipperGuidedReport,
  isSkipperGuidedUnsettledReport
} from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'
import { SKIPPER_REPORT_FEATURE } from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReportsConst'

import { onInputKeyDown } from '@aytm/app/stats/page/sidebar/components/lib/utils'

// components
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'

import Portal from '@aytm/lib/rfd-ui/components/basic/portal'
import ClickOutside from '@aytm/lib/rfd-ui/components/basic/click-outside'

import ReportPopupDescription from '@aytm/app/stats/page/sidebar/reports/components/ReportPopupDescription'
import ReportPopupSharing from '@aytm/app/stats/page/sidebar/reports/components/ReportPopupSharing'

import ReportControlsWithContext from '@aytm/app/stats/page/sidebar/reports/ReportControls'

// styles
import style from '@aytm/app/stats/page/sidebar/reports/styles/ReportPopup.module.scss'
import reportStyle from '@aytm/app/stats/page/sidebar/reports/styles/Report.module.scss'

const PORTAL_ID = 'report-popup-portal'

const TITLE_MAX = 250

// <Component>
// eslint-disable-next-line no-class-assign
class ReportPopup extends ManagedRenderComponent {
  static propTypes = {}
  static updateOnProps(nextProps, prevProps) {
    return (
      nextProps.state != prevProps.state ||
      nextProps.model != prevProps.model ||
      nextProps.position != prevProps.position ||
      nextProps.shareLinkHash != prevProps.shareLinkHash ||
      nextProps.disabled != prevProps.disabled
    )
  }

  state = {
    title: '',
    hidden: true,
    opened: false,
    focused: null,
    copied: false
  }

  constructor(props, context) {
    super(props, context)

    this.getModelProps = this.getModelProps.bind(this)
    this.scrollPopupContainer = this.scrollPopupContainer.bind(this)
    this.openPopup = this.openPopup.bind(this)
    this.closePopup = this.closePopup.bind(this)
    this.hidePopup = this.hidePopup.bind(this)
    this.focusMe = this.focusMe.bind(this)
    this.blurMe = this.blurMe.bind(this)
    this.onReportTitleChange = this.onReportTitleChange.bind(this)
    this.handleReportTitlePastedText = this.handleReportTitlePastedText.bind(this)
    this.onReportDelete = this.onReportDelete.bind(this)
    this.onReportDeleteCancel = this.onReportDeleteCancel.bind(this)
    this.updateShareLink = this.updateShareLink.bind(this)
    this.copyLink = this.copyLink.bind(this)
    this.selectValue = this.selectValue.bind(this)
    this.updateContainerPosition = this.updateContainerPosition.bind(this)
    this.updatePopupPosition = this.updatePopupPosition.bind(this)
    this.onActive = this.onActive.bind(this)
    this.offActive = this.offActive.bind(this)
    this.renderDeleteReport = this.renderDeleteReport.bind(this)
    this.renderCurrentReportVersion = this.renderCurrentReportVersion.bind(this)
    this.renderContent = this.renderContent.bind(this)

    this.timer = null
    this.updateTimer = null
    this.updatePosition = false
    this.scrollTop = 0

    this.escHandler = event => 27 == event.keyCode && this.closePopup()
    this.scrollHandler = () => this.scrollPopupContainer()
    this.positionHandler = () => this.updatePopupPosition(true)

    this.titleTimer = null

    this.state.title = props.model.get('title')
  }

  getModelProps() {
    const { url, state, model, listState, listModel, actions } = this.props

    return {
      url,
      listState,
      listModel,
      state,
      model,
      actions
    }
  }

  scrollPopupContainer() {
    const { root } = this.props

    if (root.scrollTop != this.scrollTop) {
      this.closePopup()
    }

    this.scrollTop = root.scrollTop
  }

  openPopup() {
    const { model, listState, actions } = this.props
    updateListState({
      updates: [{ path: reportStatePath(model.get('id'), 'active'), value: true }],
      listState,
      actions
    })
  }

  closePopup() {
    if (true === this.updatePosition) return

    const { model, listState, actions } = this.props
    updateListState({
      updates: [
        { path: reportStatePath(model.get('id'), 'active'), value: false },
        { path: reportStatePath(model.get('id'), 'mode'), value: null }
      ],
      listState,
      actions
    })
  }

  hidePopup() {
    this.setState({
      hidden: true
    })
  }

  focusMe(val) {
    this.setState({ focused: val })
  }

  blurMe() {
    this.setState({ focused: null })
  }

  onReportTitleChange(event) {
    const { model } = this.props

    this.titleTimer && clearTimeout(this.titleTimer)

    const text = event.target.value
    this.setState({ title: encodeText(text, false) })

    const error = !text.length || text.length > TITLE_MAX
    if (!error) {
      this.titleTimer = setTimeout(() => {
        updateItemModel({
          ...this.getModelProps(),
          data: { do: 'title', id: model.get('id'), title: encodeText(text, false) },
          updates: [{ path: ['title'], value: encodeText(text, false) }]
        })
      }, 1000)
    }
  }

  handleReportTitlePastedText(event) {
    event.preventDefault()

    const { model } = this.props

    this.titleTimer && clearTimeout(this.titleTimer)

    let text =
      (event.clipboardData || window.clipboardData).getData('Text') || event.clipboardData.getData('text/plain')

    text = text.replace(/(\r\n|\n|\r)/gm, ' ').trim()

    const el = event.currentTarget

    const l = text.length
    const start = el.selectionStart
    const end = el.selectionEnd

    const valueBefore = el.value.slice(0, start)
    const valueAfter = el.value.slice(end)

    text = `${valueBefore}${text}${valueAfter}`.slice(0, TITLE_MAX)
    this.setState({ title: encodeText(text, false) })

    const error = !text.length || text.length > TITLE_MAX
    if (!error) {
      this.titleTimer = setTimeout(() => {
        updateItemModel({
          ...this.getModelProps(),
          data: { do: 'title', id: model.get('id'), title: encodeText(text, false) },
          updates: [{ path: ['title'], value: encodeText(text, false) }]
        })
      }, 1000)
    }

    el.setSelectionRange(start + l, start + l)
    el.blur()
    el.focus()
  }

  onReportDelete() {
    deleteListItem({
      ...this.getModelProps()
    })
  }

  onReportDeleteCancel() {
    const { model, listState, actions } = this.props
    updateListState({
      updates: [{ path: reportStatePath(model.get('id'), 'mode'), value: null }],
      listState,
      actions
    })
  }

  updateShareLink(share) {
    updateItemShareLink({
      ...this.getModelProps(),
      share: share
    })
  }

  copyLink() {
    const { model } = this.props

    copyToClipboard(model.get('shareLink'))

    this.setState({ copied: true })
    setTimeout(() => this.setState({ copied: false }), 1000)
  }

  selectValue(event, val) {
    event.target.parentElement.querySelector('input').select()
    this.focusMe(val)
  }

  updateContainerPosition() {
    const { root } = this.props

    const buttonRect = this.refButton.getBoundingClientRect()
    const rootRect = root.getBoundingClientRect()

    const h = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight

    let d = 0
    if (rootRect.top > buttonRect.top) {
      d = rootRect.top - buttonRect.top
      root.scrollTop = root.scrollTop - d

      this.scrollTop = root.scrollTop
    }

    if (43 > buttonRect.top + buttonRect.height / 2 + d)
      root.scrollTop = root.scrollTop - (43 - buttonRect.top - buttonRect.height / 2 - d)
    else if (37 > h - buttonRect.top - buttonRect.height / 2 - d)
      root.scrollTop = root.scrollTop + (37 - (h - buttonRect.top - buttonRect.height / 2 - d))

    this.scrollTop = root.scrollTop
  }

  updatePopupPosition(repeat = false) {
    if (this.refPopup) {
      this.updatePosition = repeat

      this.updateContainerPosition()

      const buttonRect = this.refButton.getBoundingClientRect()
      const popupRect = this.refPopup.getBoundingClientRect()

      const h = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight

      const pm = 20
      const pb = 6
      const bt = 115
      const bb = 11

      const m = pm + pb + bt + bb

      const d = buttonRect.top + buttonRect.height / 2 + popupRect.height - h - m
      if (d <= 0) {
        this.refPopup.style.top = `${Math.max(0, buttonRect.top + buttonRect.height / 2 - m)}px`
        this.refPopupArrow.style.top = `${Math.min(bt, buttonRect.top + buttonRect.height / 2 - pm - pb - bb)}px`
      } else {
        this.refPopup.style.top = `${h - popupRect.height}px`
        this.refPopupArrow.style.top = `${d + bt}px`
      }
      this.refPopup.style.left = `${buttonRect.left + buttonRect.width + 10}px`

      this.updateTimer && clearTimeout(this.updateTimer)
      if (repeat) {
        this.updateTimer = setTimeout(() => {
          this.updatePosition = false

          if (this.state.opened) {
            this.updatePopupPosition()
          }
        }, 100)
      }
    }
  }

  onActive() {
    const { root } = this.props
    this.scrollTop = root.scrollTop

    this.updatePopupPosition()

    window.addEventListener('scroll', this.positionHandler)
    window.addEventListener('resize', this.positionHandler)

    root.addEventListener('scroll', this.scrollHandler)
  }

  offActive() {
    const { root } = this.props
    this.scrollTop = root.scrollTop

    window.removeEventListener('scroll', this.positionHandler)
    window.removeEventListener('resize', this.positionHandler)

    root.removeEventListener('scroll', this.scrollHandler)
  }

  componentDidMount() {
    if (true === this.props.state.get('active')) {
      this.setState({
        hidden: false
      })
      this.timer = setTimeout(() => {
        this.timer = null
        this.setState({
          opened: true
        })
        this.onActive()
      }, 10)
    }
  }

  componentDidUpdate(prevProps) {
    const oldProcess = prevProps.state.get('process')
    const newProcess = this.props.state.get('process')
    if (newProcess != oldProcess) {
      this.updatePopupPosition()
    }
    const oldActive = prevProps.state.get('active')
    const newActive = this.props.state.get('active')
    if (newActive != oldActive) {
      this.emails = ''
      this.timer && clearTimeout(this.timer)
      this.setState({
        [true === newActive ? 'hidden' : 'opened']: false
      })

      this.timer = setTimeout(
        () => {
          this.timer = null
          this.setState({
            [true === newActive ? 'opened' : 'hidden']: true
          })
          true === newActive ? this.onActive() : this.offActive()
        },
        true === newActive ? 10 : 250
      )

      if (true === newActive) document.addEventListener('keydown', this.escHandler, false)
      else document.removeEventListener('keydown', this.escHandler, false)
    }
  }

  componentWillUnmount() {
    this.timer && clearTimeout(this.timer)
    this.titleTimer && clearTimeout(this.titleTimer)
    this.updateTimer && clearTimeout(this.updateTimer)

    window.removeEventListener('scroll', this.positionHandler)
    window.removeEventListener('resize', this.positionHandler)

    document.removeEventListener('keydown', this.escHandler, false)

    const { root } = this.props
    root.removeEventListener('scroll', this.scrollHandler)
  }

  renderDeleteReport() {
    const { state } = this.props
    const isProcessing = state.has('process')

    return (
      <div>
        <div className={`${style['content-row']} p-fluid`}>
          <div className={`${style['content-row-column']} ${style['content-row-column-title']}`}>DELETE REPORT?</div>
        </div>
        <div className={`${style['content-row']} p-fluid`}>
          <div className={`${style['content-row-column']} ${style['content-row-column-text']}`}>
            You are about to delete this report. <b>This operation can not be undone</b>. Anyone with the share link,
            associated with this report will be redirected to aytm home page or dashboard if they are logged in users
            next time they try to access it or refresh their browser window.
            <br />
            <br />
            Do you still want to delete this report and associated share link?
          </div>
        </div>
        <div className={`${style['content-row']} p-fluid`}>
          <div
            className={`${style['content-row-column']} ${style['content-row-column-icon']} ${style['content-row-column-icon-error']}`}
          >
            <i className="icon-aytmfd-trash" />
          </div>
          <div className={`${style['content-row-column']} ${style['content-row-column-buttons']}`}>
            <div className={style['content-row-column']}>
              <Button
                className="p-button-stat"
                label="CANCEL"
                disabled={isProcessing}
                onClick={isProcessing ? null : this.onReportDeleteCancel}
              />
            </div>
            <div className={style['content-row-column']}>
              <Button
                className="p-button-stat"
                label="DELETE REPORT"
                loading={isProcessing && 'delete' == state.get('process')}
                disabled={isProcessing}
                onClick={isProcessing ? null : this.onReportDelete}
              />
            </div>
          </div>
        </div>
        <div className={`${style['content-row']} p-fluid`}>
          <div className={`${style['content-row-column']} ${style['content-row-column-text']}`}>
            <b>Alternatively</b> - if you want to block access to this report but don&apos;t wish to delete the report
            itself, click &quot;x&quot; in the field below to delete the share link. You&apos;ll be able to generate a
            new share link at any time.
          </div>
        </div>
      </div>
    )
  }

  renderCurrentReportVersion() {
    const { state, model, virtualReportsIds, isSurveyGuest, isSurveyViewer } = this.props
    const schema = model.get('schema')
    const isEditable =
      state.get('editable') &&
      !['original', 'xpert', 'widget', 'skipper', 'concept', ...virtualReportsIds].includes(model.get('type'))
    const isDisabled = !['xpert', 'xpert_custom'].includes(model.get('type')) && null !== schema && !schema.size

    return (
      <div>
        <div className={`${style['content-row']} p-fluid`}>
          <div className={`${style['content-row-column']} ${style['content-row-column-title']}`}>REPORT VERSIONS</div>
        </div>
        <div className={`${style['content-row']} p-fluid`}>
          <div className={`${style['content-row-column']} ${style['content-row-column-text']}`}>
            {isSurveyGuest || isSurveyViewer ? (
              <>
                <b>Please note:</b> your copy of the report may be outdated, comparing to the latest copy, we have
                online. Your version is stored locally for now in this browser.
                {isEditable ? (
                  <>
                    <br />
                    <br />
                    Click &quot;REVERT&quot; to replace your version with the latest one.
                  </>
                ) : null}
              </>
            ) : !isEditable &&
              !['original', 'xpert', 'widget', 'skipper', 'concept', ...virtualReportsIds].includes(
                model.get('type')
              ) ? (
              <>
                <b>Please note:</b> your copy of the report may be outdated, comparing to the latest copy, we have
                online. Your version is stored locally for now in this browser.
                <br />
                <br />
                Click &quot;UPDATE&quot; to replace your version with the latest one. Click &quot;SAVE AS A NEW
                REPORT&quot; to save your version as a clone.
              </>
            ) : (
              <>
                <b>Please note:</b> you have modified this report, comparing to the copy, we have online. Your changes
                are stored locally for now in this browser. The latest version of the report is your current view of the
                stats page.
                {isEditable && !isSurveyGuest && !isSurveyViewer ? (
                  <>
                    <br />
                    <br />
                    If you click &quot;SAVE FOR EVERYONE&quot; below, anyone with the existing share link will be able
                    to see <b>your current view</b> next time they open the link or refresh their page.
                  </>
                ) : null}
              </>
            )}
            {isDisabled ? (
              <>
                <br />
                <br />
                <b>Please add at least one question in order to save this report.</b>
              </>
            ) : null}
          </div>
        </div>
        <div className={`${style['content-row']} p-fluid`}>
          <div
            className={`${style['content-row-column']} ${style['content-row-column-icon']} ${style['content-row-column-icon-warning']}`}
          >
            <i className="icon-aytmfd-save" />
          </div>
          <ReportControlsWithContext />
        </div>
      </div>
    )
  }

  renderContent() {
    const { state, model, features, virtualReportsIds, isSurveyGuest, isSurveyViewer, shareLinkHash } = this.props
    const { title, focused, copied } = this.state
    const shareLinkExist = model.get('shareLink') && 0 < model.get('shareLink').length
    const isChanged = true === state.get('changed')
    const isProcessing = state.has('process')
    const isDeleting = 'delete' == state.get('mode')
    const isUnsettled = isSkipperGuidedUnsettledReport(model)
    const isLocked =
      ['original', 'xpert', 'widget', 'concept', ...virtualReportsIds].includes(model.get('type')) ||
      ('skipper' == model.get('type') && !isSkipperGuidedReport(model)) ||
      (features?.[SKIPPER_REPORT_FEATURE]?.disabled && isSkipperGuidedReport(model)) ||
      isUnsettled ||
      isDeleting ||
      isSurveyGuest ||
      isSurveyViewer

    return (
      <div ref={this.saveRef('Content')} className={style.content}>
        <div className={`${style['content-row']} p-fluid`}>
          <div className={`${style['content-row-column']} ${style['content-row-column-label']}`}>REPORT TITLE</div>
          <div className={`${style['content-row-column']} ${style['content-row-column-input']}`}>
            <InputTextarea
              value={decodeText(title, false)}
              maxLength={TITLE_MAX}
              rows={1}
              autoResize
              disabled={isLocked}
              onKeyDown={isLocked ? null : onInputKeyDown}
              onKeyUp={isLocked ? null : this.updatePopupPosition}
              onChange={isLocked ? null : this.onReportTitleChange}
              onPaste={isLocked ? null : this.handleReportTitlePastedText}
            />
            {isLocked ? null : (
              <span className={style['content-row-column-input-count']}>{TITLE_MAX - title.length}</span>
            )}
          </div>
        </div>
        {isDeleting ? (
          this.renderDeleteReport()
        ) : isChanged ? (
          this.renderCurrentReportVersion()
        ) : isUnsettled ? null : (
          <ReportPopupSharing onFocus={() => this.focusMe('email')} onBlur={this.blurMe} />
        )}
        {isUnsettled ? null : (
          <>
            <div className={`${style['content-row']} p-fluid`}>
              <div className={`${style['content-row-column']} ${style['content-row-column-note']}`}>
                REPORT LINK (Anyone with the link can view this report)
              </div>
            </div>
            <div
              className={`${style['content-row']}${
                'link' == focused ? ' ' + style['content-row-focused'] : ''
              } p-fluid`}
            >
              <div className={`${style['content-row-column']} ${style['content-row-column-icon']}`}>
                <i className="icon-aytmfd-link-v2" />
              </div>
              <div className={`${style['content-row-column']} ${style['content-row-column-input']}`}>
                <InputText
                  value={shareLinkExist ? `${model.get('shareLink')}${shareLinkHash}` : ''}
                  disabled={true}
                  onFocus={event => this.selectValue(event, 'link')}
                  onBlur={this.blurMe}
                />
                {isProcessing && 'unshare' == state.get('process') ? (
                  <span className={style['content-row-column-input-icon']}>
                    <i className="icon-aytmfd-refresh-v2 aytmfd-spin" />
                  </span>
                ) : shareLinkExist &&
                  !['skipper', 'concept'].includes(model.get('type')) &&
                  !isSurveyGuest &&
                  !isSurveyViewer ? (
                  <span className={style['content-row-column-input-icon']}>
                    <i className="icon-aytmfd-close-v2" onClick={() => this.updateShareLink(false)} />
                  </span>
                ) : null}
              </div>
              {virtualReportsIds.includes(model.get('type')) ? null : (
                <div className={`${style['content-row-column']} ${style['content-row-column-button']}`}>
                  {!(shareLinkExist || isSurveyGuest || isSurveyViewer) ? (
                    <Button
                      className="p-button-stat"
                      label="GENERATE"
                      loading={isProcessing && 'share' == state.get('process')}
                      disabled={isProcessing}
                      onClick={isProcessing ? null : () => this.updateShareLink(true)}
                    />
                  ) : null}
                  {shareLinkExist ? (
                    <Button
                      className="p-button-stat"
                      label={copied ? 'COPIED' : 'COPY'}
                      disabled={isProcessing || copied}
                      onClick={isProcessing || copied ? null : this.copyLink}
                    />
                  ) : null}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  render() {
    const { model, disabled } = this.props
    const { hidden, opened } = this.state

    return (
      <>
        <div
          ref={this.saveRef('Button')}
          className={`${reportStyle.popupButton} ${reportStyle['popupButton-' + (opened ? 'opened' : 'closed')]}${
            disabled ? ' ' + reportStyle['popupButton-disabled'] : ''
          }`}
          onClick={hidden && !opened && !disabled ? this.openPopup : null}
        >
          <i className="icon-aytmfd-config" />
        </div>
        {!hidden && !disabled ? (
          <Portal nodeID={`${PORTAL_ID}-${model.get('id')}`}>
            <ClickOutside onClickOutside={this.closePopup}>
              <Transition in={opened} timeout={250} onExited={this.hidePopup}>
                {status => (
                  <div
                    ref={this.saveRef('Popup')}
                    className={`PRAYTMTheme ${style['transition-panel']} ${
                      status ? style['transition-panel-' + status] : ''
                    }`}
                  >
                    <div className={`${style['component']} ${status ? style['component-' + status] : ''}`}>
                      <div ref={this.saveRef('PopupArrow')} className={style.arrow} />
                      <div className={style.closeButton} onClick={this.closePopup}>
                        <i className="icon-aytmfd-close-v2" />
                      </div>
                      <ReportPopupDescription />
                      {this.renderContent()}
                    </div>
                  </div>
                )}
              </Transition>
            </ClickOutside>
          </Portal>
        ) : null}
      </>
    )
  }
}

ReportPopup = ContextSubscriber(
  {
    name: 'report',
    contextTypes: {
      state: PropTypes.instanceOf(Map).isRequired,
      model: PropTypes.instanceOf(Map).isRequired,
      position: PropTypes.number.isRequired
    },
    mapToProps(values) {
      return {
        state: values.state,
        model: values.model,
        position: values.position
      }
    }
  },
  {
    name: 'reportList',
    contextTypes: {
      root: PropTypes.any.isRequired,
      url: PropTypes.string.isRequired,
      state: PropTypes.instanceOf(Map).isRequired,
      model: PropTypes.instanceOf(Map).isRequired,
      actions: PropTypes.object.isRequired,
      features: PropTypes.object,
      currentReportId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      virtualReportsIds: PropTypes.array.isRequired,
      shareLinkHash: PropTypes.string,
      timeZone: PropTypes.instanceOf(Map),
      disabled: PropTypes.bool,
      isSurveyOwner: PropTypes.bool,
      isSurveyGuest: PropTypes.bool,
      isSurveyViewer: PropTypes.bool
    },
    mapToProps(values) {
      return {
        root: values.root,
        url: values.url,
        listState: values.state,
        listModel: values.model,
        actions: values.actions,
        features: values.features,
        currentReportId: values.currentReportId,
        virtualReportsIds: values.virtualReportsIds,
        shareLinkHash: values.shareLinkHash,
        timeZone: values.timeZone,
        disabled: values.disabled,
        isSurveyOwner: values.isSurveyOwner,
        isSurveyGuest: values.isSurveyGuest,
        isSurveyViewer: values.isSurveyViewer
      }
    }
  }
)(ReportPopup)
export default ReportPopup
